import axios, { AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:5000/api';

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('admin_token');
};

const setToken = (token: string): void => {
  localStorage.setItem('admin_token', token);
};

const removeToken = (): void => {
  localStorage.removeItem('admin_token');
};

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    language: string;
    isActive: boolean;
    role: 'admin' | 'bearbeiter';
  };
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  language: string;
  is_active: boolean;
  role: 'admin' | 'bearbeiter';
  created_at?: string;
  updated_at?: string;
}

export interface Appointment {
  id: number;
  patient_name: string;
  patient_email: string;
  patient_phone: string;
  provider_id: number;
  provider_name: string;
  procedure_id: number;
  procedure_name: string;
  operatory_id: number;
  operatory_name: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  language: string;
  google_calendar_event_id?: string;
  source?: 'admin' | 'website';
  insurance?: string;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: number;
  appointment_id: number;
  action: string;
  old_data: any;
  new_data: any;
  changed_by: string;
  changed_at: string;
}

export type ProviderRole = 'dentist' | 'hygienist';

export interface Provider {
  id: number;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  role: ProviderRole;
  google_calendar_id?: string;
  is_active: boolean;
}

export type LocalizedString = Record<'de' | 'en' | 'es' | 'ru', string>;

export interface Procedure {
  id: number;
  key?: string;
  name: LocalizedString | string;       // multilingual; string for legacy
  description?: LocalizedString | string;
  duration: number;
  color: string;
  required_roles?: ProviderRole[];
  is_active: boolean;
  bookable?: boolean;
}

/** Resolve a localized string in given locale, with fallback chain. */
export const resolveLocalized = (v: LocalizedString | string | undefined, locale: string = 'de'): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  const obj = v as Record<string, string>;
  return obj[locale] || obj.de || obj.en || obj.es || obj.ru || Object.values(obj).find(Boolean) || '';
};

export interface Operatory {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

export interface ConflictCheckParams {
  date: string;
  startTime: string;
  endTime: string;
  providerId: number;
  operatoryId: number;
  excludeId?: number;
}

class ApiService {
  private api;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          removeToken();
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginCredentials): Promise<AxiosResponse<LoginResponse>> {
    const response = await this.api.post('/auth/login', credentials);
    if (response.data.success && response.data.token) {
      setToken(response.data.token);
    }
    return response;
  }

  async requestMagicLink(email: string): Promise<AxiosResponse<{ success: boolean; message?: string }>> {
    return this.api.post('/auth/magic-link/request', { email });
  }

  async verifyMagicLink(token: string): Promise<AxiosResponse<Partial<LoginResponse> & { success: boolean; message?: string; code?: string }>> {
    const response = await this.api.post('/auth/magic-link/verify', { token });
    if (response.data.success && response.data.token) {
      setToken(response.data.token);
    }
    return response;
  }

  logout(): void {
    removeToken();
  }

  isAuthenticated(): boolean {
    return !!getToken();
  }

  // Appointment methods
  async getAppointments(params?: any): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments', { params });
  }

  async getAppointment(id: number): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.get(`/appointments/${id}`);
  }

  async getAppointmentAuditLogs(id: number): Promise<AxiosResponse<ApiResponse<AuditLog[]>>> {
    return this.api.get(`/appointments/${id}/audit-logs`);
  }

  async createAppointment(appointmentData: Partial<Appointment>): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.post('/appointments', appointmentData);
  }

  async updateAppointment(id: number, appointmentData: Partial<Appointment>): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.put(`/appointments/${id}`, appointmentData);
  }

  async deleteAppointment(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/appointments/${id}`);
  }

  async cancelAppointment(id: number): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.patch(`/appointments/${id}/cancel`);
  }

  async checkConflicts(params: ConflictCheckParams): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments/conflicts', { params });
  }

  // Provider methods
  async getProviders(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Provider[]>>> {
    return this.api.get('/providers', { params: { activeOnly } });
  }

  async getProvider(id: number): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.get(`/providers/${id}`);
  }

  async createProvider(providerData: Partial<Provider>): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.post('/providers', providerData);
  }

  async updateProvider(id: number, providerData: Partial<Provider>): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.put(`/providers/${id}`, providerData);
  }

  async deleteProvider(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/providers/${id}`);
  }

  // Procedure methods
  async getProcedures(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Procedure[]>>> {
    return this.api.get('/procedures', { params: { activeOnly } });
  }

  async getProcedure(id: number): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.get(`/procedures/${id}`);
  }

  async createProcedure(procedureData: Partial<Procedure>): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.post('/procedures', procedureData);
  }

  async updateProcedure(id: number, procedureData: Partial<Procedure>): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.put(`/procedures/${id}`, procedureData);
  }

  async deleteProcedure(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/procedures/${id}`);
  }

  // Operatory methods
  async getOperatories(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Operatory[]>>> {
    return this.api.get('/operatories', { params: { activeOnly } });
  }

  async getOperatory(id: number): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.get(`/operatories/${id}`);
  }

  async createOperatory(operatoryData: Partial<Operatory>): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.post('/operatories', operatoryData);
  }

  async updateOperatory(id: number, operatoryData: Partial<Operatory>): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.put(`/operatories/${id}`, operatoryData);
  }

  async deleteOperatory(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/operatories/${id}`);
  }

  // Google Calendar methods
  async syncGoogleCalendar(): Promise<AxiosResponse<any>> {
    return this.api.post('/google-calendar/sync');
  }

  // DEBUG: Fetch raw Google Calendar events
  async debugGoogleCalendarEvents(): Promise<AxiosResponse<any>> {
    return this.api.get('/google-calendar/debug-events');
  }

  // Share calendar with user's email
  async shareGoogleCalendar(userEmail: string, role: string = 'reader'): Promise<AxiosResponse<any>> {
    return this.api.post('/google-calendar/share-calendar', { userEmail, role });
  }

  // Get calendar permissions
  async getGoogleCalendarPermissions(): Promise<AxiosResponse<any>> {
    return this.api.get('/google-calendar/permissions');
  }

  // Additional utility methods for backward compatibility
  async getUpcomingAppointments(days = 7, providerId?: number): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments/upcoming', { params: { days, providerId } });
  }

  setAuthToken(token: string): void {
    setToken(token);
  }

  removeAuthToken(): void {
    removeToken();
  }

  // Generic methods for backward compatibility
  async get<T>(url: string, params?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.get(url, { params });
  }

  async post<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.post(url, data);
  }

  async put<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.put(url, data);
  }

  async patch<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.patch(url, data);
  }

  async delete<T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.delete(url);
  }
}

export const apiService = new ApiService();
export default apiService; 